import React, { FunctionComponent, memo } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { IconDefinition } from '@fortawesome/fontawesome';
import { IconTypes, iIcon } from '../../shared/interfaces';
import { Fa } from '../elements/fa';
import { DeviceSvgIcon } from './components/DeviceSvgIcon';

interface SvgIconProps {
  icon: iIcon;
  color?: string;
  size?: IconSize;
  style?: React.CSSProperties;
}

export enum IconSize {
  XXS = 'xxs',
  XXS_25 = 'xxs-25',
  XS = 'xs',
  SM = 'sm',
  SM_25 = 'sm-25',
  MD = 'md',
  LG = 'lg',
}

const convertSizeToEm = (size: IconSize): number => {
  switch (size) {
    case 'xxs':
      return 1;
    case 'xxs-25':
      return 1.25;
    case 'xs':
      return 1.2;
    case 'sm':
      return 2;
    case 'sm-25':
      return 2.5;
    case 'md':
      return 3;
    case 'lg':
      return 4.5;
  }
};

const determineIconType = (icon: iIcon) => {
  if (typeof icon == 'object' && 'fa' in icon) {
    return IconTypes.FaIcon;
  } else if (typeof icon == 'object' && 'svgIconName' in icon) {
    return IconTypes.SvgIcon;
  } else if (typeof icon == 'object' && 'url' in icon) {
    return IconTypes.UserSvgIcon;
  }

  return null;
};

const SvgIcon: FunctionComponent<SvgIconProps> = ({
  icon,
  color = '#000000',
  size = IconSize.XS,
}) => {
  const iconType = icon?.type ?? determineIconType(icon);

  const sizeEm = `${convertSizeToEm(size)}em`;

  switch (iconType) {
    case IconTypes.FaIcon:
      return (
        <Fa
          icon={icon?.fa as IconDefinition}
          style={{ color, fontSize: sizeEm }}
        />
      );
    case IconTypes.SvgIcon:
      return (
        <DeviceSvgIcon
          name={icon?.svgIconName as string}
          width={sizeEm}
          height={sizeEm}
          fill={color}
        />
      );
    case IconTypes.UserSvgIcon:
      return (
        <img
          src={icon?.url as string}
          style={{ width: sizeEm, height: sizeEm, fill: color, color }}
        />
      );
    default:
      return (
        <Box sx={{ display: 'flex' }}>
          <CircularProgress />
        </Box>
      );
  }
};


export default memo(SvgIcon);
